import { Box, ITheme, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Utilities } from '../../../utility/utils';
import { useTranslation } from 'react-i18next';
import { IClosingFund } from '../../../models/Fund';
import { LeoImagesFundsPath } from '../../../resources/api-constants';
import FundDetails from '../../libraryOfFunds/FundDetails';

export interface ILatestsFundsCarouselProps {
    orientation: 'Horizontal' | 'Vertical'
    type: 'closingValue' | 'closingDate' | 'comingDate';
    data: IClosingFund[];
    icon: string;
}

const LatestsFundsCarousel: React.FC<ILatestsFundsCarouselProps> = ({ type, data, icon }) => {
    const theme = useTheme() as ITheme;
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [pauseAnimation, setPauseAnimation] = useState(false);
    const [showItem, setShowItem] = useState<number | undefined>(undefined);
    const [isAnimating, setIsAnimating] = useState<boolean>();
    const [viewShift, setShift] = useState<number>(0);
    const shift = useRef(0);
    const way = useRef(1);

    const timeoutId = useRef<NodeJS.Timeout | undefined>();


    const onNextItem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        // setSelectedIndex((selectedIndex + 1) % data.length);
        goNextItem();
    }
    const onPreviousItem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        let newIndex = selectedIndex - 1;
        if (newIndex < 0) {
            newIndex += data.length;
        }
        goPreviousItem();
    }

    const onHoverEnter = () => {
        setPauseAnimation(true);
    }
    const onHoverLeave = () => {
        setPauseAnimation(false);
    }

    const updateShifting = () => {
        setTimeout(() => {
            const newShift = shift.current + 0.02;
            if (newShift >= 1) {
                shift.current = 0
                setShift(shift.current);
                setSelectedIndex(index => (data.length + index + way.current) % data.length)
                setIsAnimating(false);
            } else {
                shift.current = newShift;
                setShift(shift.current);
                updateShifting();
            }
        }, 1);
    }

    const goNextItem = () => {
        setIsAnimating(true);
        way.current = 1;
        shift.current = 0;
        updateShifting();
    }
    const goPreviousItem = () => {
        setIsAnimating(true);
        way.current = -1;
        shift.current = 0;
        updateShifting();
    }

    useEffect(() => {
        if (pauseAnimation) {
            if (timeoutId.current !== undefined) {
                clearInterval(timeoutId.current);
                timeoutId.current = undefined
            }
        } else {
            if (timeoutId.current === undefined) {
                timeoutId.current = setInterval(() => {
                    //setSelectedIndex(index => (index + 1) % data.length)
                    goNextItem();
                }, 5000);
            }
        }
    }, [pauseAnimation])

    const renderItem = (itm: IClosingFund, margin: number): React.ReactNode => {
        return <Box display={"inline-flex"} flexDirection={"column"} bgcolor={theme.palette.common.white} width={"100%"} borderRadius={"8px"} flexShrink={0} key={`fund-carousel-item-${itm.id}`} sx={{ cursor: "pointer", marginLeft: `-${margin*100}%`}} onClick={() => setShowItem(itm.id)}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} margin={"14px 19.5px 0 19.5px"}>
                <img src={icon} style={{ width: "38px", height: "38px" }} alt={t('A11yFactsAboutFundsLogo')} />
                <Typography fontSize={"14px"} lineHeight={"17.07px"} fontWeight={"600"} color={type === 'comingDate' ? theme.palette.leo.semantic.green : theme.palette.leo.color1}>{type === 'comingDate' ? t('DashboardComingSoonTitle') : t('DashboardClosingSoonTitle')}</Typography>
                {/* <Typography fontSize={"12px"} lineHeight={"20px"} fontWeight={"500"} color={theme.palette.primary.main}>{type === 'days' ? t('DashboardClosingSoonInDays') : t('DashboardClosingSoonInEuros')}</Typography> */}
            </Box>
            <Box display={"flex"} flexDirection={"column"} margin={"5px 19.5px 10px 19.5px"} gap={"6px"}>
                <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                    <IconButton onClick={onPreviousItem} aria-label={t('A11yPreviousFundButton')}>
                        <KeyboardArrowLeftIcon sx={{ color: theme.palette.primary.dark }} />
                    </IconButton>
                    <img src={`${LeoImagesFundsPath}/${itm.logo}`} style={{ maxWidth: "150px", maxHeight: "100%" }} alt={`${t('A11yFundLogoAlt')} ${itm.name}`} />
                    <IconButton onClick={onNextItem} aria-label={t('A11yNextFundButton')}>
                        <KeyboardArrowRightIcon sx={{ color: theme.palette.primary.dark }} />
                    </IconButton>
                </Box>
                <Box display={"flex"} gap={"15px"} alignItems={"center"} justifyContent={"space-between"}>
                    <Box display={"flex"} flexDirection={"column"} gap={"4px"}>
                        <Typography fontSize={"14px"} lineHeight={"20px"} fontWeight={"600"} color={type === 'comingDate' ? theme.palette.leo.semantic.green : theme.palette.leo.color1}>
                            {type === 'closingDate' ?
                                <>{itm.closingIn} {t('DashboardClosingSoonDaysLeft')}</>
                                :
                                type === 'closingValue' ?
                                    <>€{Utilities.toFriendlyMoneyValue(itm.closingIn)} {t('DashboardClosingSoonEurosLeft')}</>
                                    :
                                    <>{`${t('DashboardFundFor')} ${itm.closingIn} ${t('DashboardFundDays')}`}</>
                            }
                        </Typography>
                        <Typography fontSize={"16px"} lineHeight={"17.07px"} fontWeight={"700"} letterSpacing={"0.5px"} color={theme.palette.text.primary}>{itm.name}</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"flex-end"} flexBasis={"30%"} >
                        <Typography fontSize={"18px"} lineHeight={"21.94px"} fontWeight={"600"} letterSpacing={"0.5px"} color={theme.palette.text.primary}>€{Utilities.toFriendlyMoneyValue(itm.fondSize)}</Typography>
                        <Typography fontSize={"10px"} lineHeight={"20px"} fontWeight={"500"} letterSpacing={"0.5px"} color={theme.palette.grey["500"]}>{t('DashboardClosingSoonFundSize')}</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>;
    }

    return <Box width={"100%"} borderRadius={"8px"} overflow={"hidden"} display={"flex"} className={`carousel`} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
        {isAnimating && way.current === -1 && renderItem(data[(data.length + selectedIndex - 1) % data.length],1-viewShift)}
        {renderItem(data[selectedIndex], way.current === 1 ? viewShift : 0)}
        {isAnimating && way.current === 1 && renderItem(data[(selectedIndex + 1) % data.length],0)}
        {/* {data.map((itm) => (
        ))} */}
        {showItem !== undefined && <FundDetails displayItem={showItem} onClose={() => setShowItem(undefined)} showPopin />}
    </Box>
}

export default LatestsFundsCarousel;